import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, XCircle, AlertCircle, Euro, User, Mail, FileText, ArrowRight } from 'lucide-react';

const STATUS_COLORS = {
  draft: 'badge-gray', pending: 'badge-gold', published: 'badge-green',
  rejected: 'badge-red', approved: 'badge-blue', initiated: 'badge-gray'
};

export default function Payments() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const cancelled = params.get('cancelled');

  const [fees, setFees] = useState({});
  const [loading, setLoading] = useState(false);
  const [payStatus, setPayStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState({ service_type: '', user_name: '', user_email: '', service_notes: '' });

  useEffect(() => {
    api.get('/payments/fees').then(r => setFees(r.data || {}));
  }, []);

  // Poll payment status on return from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 5;
    setChecking(true);
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        setPayStatus(res.data);
        if (res.data.payment_status === 'paid') { setChecking(false); toast.success('Pagamento confirmado!'); return; }
        if (res.data.status === 'expired') { setChecking(false); return; }
      } catch { }
      attempts++;
      if (attempts < maxAttempts) setTimeout(poll, 2000);
      else setChecking(false);
    };
    poll();
  }, [sessionId]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!form.service_type || !form.user_name || !form.user_email) {
      toast.error('Por favor preencha todos os campos obrigatórios');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/payments/checkout', {
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar pagamento');
      setLoading(false);
    }
  };

  if (sessionId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          {checking ? (
            <div>
              <div className="w-12 h-12 border-3 border-gray-200 border-t-[#CC0000] rounded-full animate-spin mx-auto mb-4" />
              <h2 className="text-lg font-bold mb-2">A verificar pagamento...</h2>
              <p className="text-sm text-gray-500">{t('payments.processing')}</p>
            </div>
          ) : payStatus?.payment_status === 'paid' ? (
            <div data-testid="payment-success">
              <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('payments.success_title')}</h2>
              <p className="text-sm text-gray-500 mb-4">{t('payments.success_msg')}</p>
              <div className="bg-gray-50 rounded-lg p-4 text-left text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">Serviço:</span>
                  <span className="font-medium">{payStatus.service_name}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">Valor:</span>
                  <span className="font-bold text-[#CC0000]">€{payStatus.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Referência:</span>
                  <span className="font-mono text-xs">{sessionId?.slice(0, 20)}...</span>
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="payment-failed">
              <XCircle size={48} className="text-gray-400 mx-auto mb-4" />
              <h2 className="text-lg font-bold mb-2">Verificação Pendente</h2>
              <p className="text-sm text-gray-500">Ref: {sessionId?.slice(0, 20)}...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <AlertCircle size={48} className="text-orange-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Pagamento Cancelado</h2>
          <p className="text-sm text-gray-500 mb-6">{t('payments.cancel_msg')}</p>
          <button onClick={() => window.location.href = '/pagamentos'}
            className="px-6 py-2.5 bg-[#CC0000] text-white font-medium rounded-md hover:bg-[#A30000] transition-colors text-sm">
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="flex items-center gap-3 mb-3">
            <CreditCard size={24} className="text-[#CC0000]" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{t('payments.title')}</h1>
          </div>
          <p className="text-gray-500 text-sm">{t('payments.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Fee Table */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Tabela de Taxas Consulares</h2>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full" data-testid="fees-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Serviço</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Taxa</th>
                    <th className="text-right px-4 py-3 hidden sm:table-cell text-xs font-semibold text-gray-500 uppercase">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(fees).map(([key, fee]) => (
                    <tr key={key} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-sm text-gray-900">{fee.name_pt}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fee.name_es}</p>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="font-bold text-[#CC0000] flex items-center justify-end gap-0.5">
                          <Euro size={13} />{fee.amount.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right hidden sm:table-cell">
                        <button onClick={() => setForm(f => ({...f, service_type: key}))}
                          data-testid={`select-${key}`}
                          className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${form.service_type === key ? 'bg-[#CC0000] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {form.service_type === key ? 'Selecionado' : 'Selecionar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Form */}
          <div>
            <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-20">
              <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                <CreditCard size={18} className="text-[#CC0000]" /> Pagar Agora
              </h2>
              <form onSubmit={handlePayment} className="space-y-4" data-testid="payment-form">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('payments.select_service')} *</label>
                  <select value={form.service_type} onChange={e => setForm(f => ({...f, service_type: e.target.value}))}
                    required data-testid="payment-service-select"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white">
                    <option value="">-- Selecione --</option>
                    {Object.entries(fees).map(([k, f]) => (
                      <option key={k} value={k}>{f.name_pt} — €{f.amount.toFixed(2)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('payments.your_name')} *</label>
                  <div className="relative">
                    <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={form.user_name} onChange={e => setForm(f => ({...f, user_name: e.target.value}))}
                      required placeholder="Nome completo" data-testid="payment-name"
                      className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('payments.your_email')} *</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="email" value={form.user_email} onChange={e => setForm(f => ({...f, user_email: e.target.value}))}
                      required placeholder="email@exemplo.com" data-testid="payment-email"
                      className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('payments.notes')}</label>
                  <div className="relative">
                    <FileText size={14} className="absolute left-3 top-3 text-gray-400" />
                    <textarea value={form.service_notes} onChange={e => setForm(f => ({...f, service_notes: e.target.value}))}
                      placeholder="Informações adicionais..." rows={2} data-testid="payment-notes"
                      className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none" />
                  </div>
                </div>
                {form.service_type && fees[form.service_type] && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{fees[form.service_type].name_pt}</span>
                      <span className="font-bold text-[#CC0000] flex items-center gap-0.5">
                        <Euro size={13} />{fees[form.service_type].amount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                <button type="submit" disabled={loading} data-testid="payment-submit"
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#CC0000] text-white font-bold rounded-md hover:bg-[#A30000] disabled:opacity-60 transition-colors">
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <CreditCard size={16} />}
                  {loading ? t('payments.processing') : t('payments.pay_now')}
                </button>
                <p className="text-xs text-gray-400 text-center">Pagamento seguro via Stripe. Os seus dados estão protegidos.</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
